import { useEffect, useMemo, useState } from "react";
import { api, inr } from "../../lib/api";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { Search, Repeat, Users as UsersIcon, TrendingUp } from "lucide-react";

const SEG_COLORS = { VIP:"#D4AF37", Loyal:"#8B7355", Active:"#2E4A35", New:"#3B2F2F", "At Risk":"#993322" };
const BADGE = { VIP:"default", Loyal:"secondary", Active:"secondary", New:"outline", "At Risk":"destructive" };

export default function AdminCustomers() {
  const [data, setData] = useState({ customers: [], segments: [] });
  const [q, setQ] = useState("");
  const [repeatOnly, setRepeatOnly] = useState(false);
  useEffect(() => { api.get("/admin/analytics/customers").then(r=>setData(r.data)); }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (data.customers || []).filter(c => {
      if (repeatOnly && (c.orders || 0) < 2) return false;
      if (!term) return true;
      return [c.name, c.email, c.phone, c.address_short]
        .some(v => (v || "").toLowerCase().includes(term));
    });
  }, [data.customers, q, repeatOnly]);

  const repeatCount = (data.customers || []).filter(c => (c.orders || 0) >= 2).length;
  const totalOrders = (data.customers || []).reduce((s, c) => s + (c.orders || 0), 0);
  const repeatRate = data.customers?.length ? Math.round((repeatCount / data.customers.length) * 100) : 0;

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-outfit font-bold">Customers</h1>
          <div className="text-sm text-stone-500">
            {data.customers.length} customers · {totalOrders} total orders · {repeatCount} repeaters ({repeatRate}%)
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm cursor-pointer bg-white border border-stone-200 rounded-full px-4 py-2 hover:border-[#D4AF37]">
            <input type="checkbox" checked={repeatOnly} onChange={e=>setRepeatOnly(e.target.checked)}
                   data-testid="repeat-only-toggle"
                   className="w-4 h-4 accent-[#3B2F2F]"/>
            <Repeat className="w-3.5 h-3.5 text-[#D4AF37]"/>
            <span>Repeat buyers only</span>
          </label>
          <div className="relative">
            <Search className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2"/>
            <Input placeholder="Search name / phone / email…" value={q} onChange={e=>setQ(e.target.value)}
                   data-testid="customer-search" className="pl-9 w-64"/>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <Kpi icon={UsersIcon} label="Total customers" value={data.customers.length} tint="#3B2F2F"/>
        <Kpi icon={Repeat} label="Repeat buyers" value={`${repeatCount}`} sub={`${repeatRate}% repeat rate`} tint="#2E4A35"/>
        <Kpi icon={TrendingUp} label="Total orders" value={totalOrders} tint="#D4AF37"/>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <div className="md:col-span-1 bg-white border border-stone-200 rounded-lg p-6">
          <h3 className="font-outfit font-semibold mb-4">Segments</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={data.segments} dataKey="count" nameKey="segment" innerRadius={40} outerRadius={80}>
                {data.segments.map((s,i)=><Cell key={i} fill={SEG_COLORS[s.segment]||"#78716C"}/>)}
              </Pie>
              <Legend wrapperStyle={{fontSize:11}}/><Tooltip contentStyle={{fontSize:12}}/>
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="md:col-span-2 bg-white border border-stone-200 rounded-lg p-6">
          <h3 className="font-outfit font-semibold mb-4">Segment breakdown</h3>
          <div className="grid grid-cols-2 gap-3">
            {data.segments.map(s => (
              <div key={s.segment} className="border border-stone-200 rounded-md p-3">
                <div className="text-xs text-stone-500">{s.segment}</div>
                <div className="text-2xl font-outfit font-bold" style={{color:SEG_COLORS[s.segment]}}>{s.count}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Customer</TableHead>
            <TableHead>Contact</TableHead>
            <TableHead>Delivery</TableHead>
            <TableHead className="text-right">Orders</TableHead>
            <TableHead className="text-right">Revenue</TableHead>
            <TableHead>Last order</TableHead>
            <TableHead>Segment</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filtered.map(c => (
              <TableRow key={c.key} data-testid={`cust-row-${c.key}`}>
                <TableCell className="text-sm">
                  <div className="font-medium">{c.name || "—"}</div>
                </TableCell>
                <TableCell className="text-xs">
                  <div className="font-mono text-stone-700">{c.phone || <span className="text-stone-300">—</span>}</div>
                  {c.email && <div className="text-stone-500 truncate max-w-[14rem]" title={c.email}>{c.email}</div>}
                </TableCell>
                <TableCell className="text-xs text-stone-600 max-w-[14rem] truncate" title={c.address_short}>
                  {c.address_short || <span className="text-stone-300">—</span>}
                </TableCell>
                <TableCell className="text-right">
                  <span className={`inline-flex items-center gap-1 font-outfit font-bold ${c.orders >= 2 ? "text-[#2E4A35]" : "text-stone-700"}`}>
                    {c.orders}
                    {c.orders >= 2 && <Repeat className="w-3 h-3"/>}
                  </span>
                </TableCell>
                <TableCell className="font-outfit text-right">{inr(c.revenue)}</TableCell>
                <TableCell className="text-xs text-stone-500">{c.days_since>999?"—":`${c.days_since}d ago`}</TableCell>
                <TableCell><Badge variant={BADGE[c.segment]||"outline"}>{c.segment}</Badge></TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-stone-500 py-10">No customers match your filter.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, tint }) {
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-5 flex items-center gap-4">
      <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{background: `${tint}18`, color: tint}}>
        <Icon className="w-5 h-5"/>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-stone-500 font-outfit font-semibold">{label}</div>
        <div className="text-2xl font-outfit font-bold" style={{color: tint}}>{value}</div>
        {sub && <div className="text-[11px] text-stone-500 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}
